import React, { useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import useImages from 'api/useImages';
import useOffers from 'api/useOffers';
import usePermissions from 'api/usePermissions';
import {
  Offer,
  ProgressBarPoint,
  RollingOfferProductSequence
} from 'common/contracts';
import {
  MIN_ROLLING_SUB_OFFERS
} from 'constants/constants';
import { ERollingOfferFormState, OfferType } from 'constants/enums';
import { Rolling } from 'design-system/AcSidebar/constants';
import { useFormik } from 'formik';
import { useTabTracking } from 'hooks/useTabTracking';
import { errorResponse } from 'utils/errorsTextHelper';
import { useOfferDesignOptions } from 'views/Offers/hooks/useOfferDesignOptions';
import { useOfferFormData } from 'views/Offers/hooks/useOfferFormData';
import { useOfferFormSubmit } from 'views/Offers/hooks/useOfferFormSubmit';
import {
  buildBaseOfferPayload,
  buildSchedulePayload,
  checkUniqueness
} from 'views/Offers/Offer.utils';
import {
  nameFieldWithTransformSchema,
  offerUiIdFieldSchema,
  priceDiscountFieldSchema,
  priorityFieldSchema,
  productSaleFieldSchema,
  publisherOfferIdWithTransformFieldSchema,
  rollingOfferPriceFieldSchema,
  setAsFreeFieldSchema
} from 'views/Offers/Offer.validation';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';
import * as yup from 'yup';

import { OfferFormLayout } from 'views/Offers/components/OfferFormLayout';
import FormSectionsTabs from 'components/FormSectionsTabs';
import { Tabs, TabsContent } from 'components/ui/tabs';

import { DEFAULT_OFFER_DESIGN_EXTERNAL_ID } from 'views/Offers/Offer.consts';
import { IRollingOfferFormProps } from './RollingOfferForm.types';
import {
  buildRollingOfferFormInitialValues,
  convertPriceFromCents,
  convertPriceToCents
} from './RollingOfferForm.utils';

import RollingOfferMock from './components/Sections/Mock';
import RollingOfferModals from './components/Sections/Modals';
import ConfigurationTab from './components/Tabs/ConfigurationTab';
import ScheduleTab from './components/Tabs/ScheduleTab';
import SettingsTab from './components/Tabs/SettingsTab';

const RollingOfferForm: React.FC<IRollingOfferFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId } = useParams();
  const { rollingOfferId } = useParams();
  const navigate = useNavigate();
  const { canUserEdit } = usePermissions();

  const { addOffer, updateOffer, getOffer, getOffers } = useOffers({
    offerId: rollingOfferId,
    offerType: OfferType.ROLLING_OFFER
  });
  const { submitOffer } = useOfferFormSubmit({
    edit,
    dup,
    offerLabel: 'Rolling Offer',
    addOffer,
    updateOffer,
    formatError: errorResponse
  });
  const { data, isLoading, chosenSegment, setChosenSegment } = useOfferFormData({
    offerId: rollingOfferId,
    publisherId,
    getOffer,
    onDataLoaded: (result: Partial<Offer>): void => {
      setCurrentOfferUI(result.offerUi?._id || '');
      if (result.productsSequence) {
        setProductsSequence(
          (result.productsSequence as RollingOfferProductSequence[]).map(
            (item) => ({
              ...item,
              priceInUsdCents: convertPriceFromCents(item?.priceInUsdCents || 0)
            })
          )
        );
      }
    }
  });
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [tab, setTab] = useState(ERollingOfferFormState.GENERAL);
  const {
    offerDesignOptions,
    defaultOfferDesignId,
    currentOfferUI,
    setCurrentOfferUI
  } = useOfferDesignOptions({
    publisherId: publisherId || '',
    offerDesignType: OfferDesignTypes.RollingOffer,
    defaultExternalId: DEFAULT_OFFER_DESIGN_EXTERNAL_ID[OfferType.ROLLING_OFFER],
    offerId: rollingOfferId
  });
  const { getImages } = useImages(publisherId);
  const [productsSequence, setProductsSequence] = useState<
    RollingOfferProductSequence[]
  >([]);
  const [barPointsValidity, setBarPointsValidity] = useState<
    Record<number, boolean>
  >({});

  const isUnique = (value: string | undefined, field: keyof Offer): boolean =>
    checkUniqueness(value, field, getOffers.data?.offers, data, !!edit);

  const offerSchema = yup.object().shape({
    name: nameFieldWithTransformSchema(isUnique),
    offerUiId: offerUiIdFieldSchema(),
    publisherOfferId: publisherOfferIdWithTransformFieldSchema(isUnique),
    setAsFree: setAsFreeFieldSchema(),
    price: rollingOfferPriceFieldSchema(),
    priority: priorityFieldSchema(),
    priceDiscount: priceDiscountFieldSchema(),
    productSale: productSaleFieldSchema()
  });

  const {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    errors,
    touched,
    setFieldValue,
    setTouched,
    validateField,
    validateForm
  } = useFormik({
    validateOnMount: true,
    validationSchema: offerSchema,
    enableReinitialize: true,
    initialValues: buildRollingOfferFormInitialValues({
      data,
      edit,
      dup,
      defaultOfferDesignId,
      getOfferResult: getOffer?.data?.result
    }),
    onSubmit: async (values) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newOffer: Record<string, any> = {
        ...buildBaseOfferPayload(values, chosenSegment, OfferType.ROLLING_OFFER),
        priority: values.priority,
        schedule: buildSchedulePayload(values.schedule),
        productsSequence: productsSequence.length
          ? productsSequence.map((seq) => {
              const progressBarPoints: ProgressBarPoint[] | undefined =
                seq.progressBarPoints?.[0]?.barId &&
                seq.progressBarPoints?.[0]?.points
                  ? [
                      {
                        points: seq.progressBarPoints?.[0]?.points,
                        barId: seq.progressBarPoints?.[0]?.barId
                      }
                    ]
                  : [];

              const hasValidProducts = seq.products.some(
                (product) => product?.product?.productId
              );
              const products = hasValidProducts
                ? seq.products.map((product) => ({
                    productId: product?.product?.productId,
                    quantity: product.quantity,
                    publisherProductId: product.product?.publisherProductId
                  }))
                : [];
              return {
                index: seq.index || 1,
                products,
                displayName: seq.displayName || values.name,
                progressBarPoints,
                priceInUsdCents: convertPriceToCents(seq?.priceInUsdCents || 0),
                ...(seq?.priceDiscount?.discount !== 0 && {
                  priceDiscount: seq?.priceDiscount
                }),
                ...(seq?.productSale?.sale !== 0 && {
                  productSale: seq?.productSale
                }),
                ...(seq?.badges?.length && {
                  badges: [
                    {
                      badgeId: seq.badges[0]?.badge?.badgeId,
                      publisherBadgeId: seq.badges[0]?.badge?.publisherBadgeId
                    }
                  ]
                })
              };
            })
          : []
      };
      submitOffer(newOffer, rollingOfferId);
    }
  });

  useEffect(() => {
    if (!productsSequence?.length) {
      const initialProductsSequence = Array.from(
        { length: MIN_ROLLING_SUB_OFFERS },
        (_, i) => ({
          index: i + 1,
          priceInUsdCents: 0,
          displayName: values.name,
          products: [
            {
              product: {
                images: []
              }
            }
          ]
        })
      );
      setProductsSequence(initialProductsSequence);
    }
  }, [productsSequence, setProductsSequence, values.name]);

  const hasMissingProductDetails = useMemo(() => {
    return productsSequence.some(
      ({ products }) =>
        !products.length ||
        products.some(
          ({ product, quantity }) => !product?.publisherProductId || !quantity
        )
    );
  }, [productsSequence]);

  useEffect(() => {
    setCurrentOfferUI(values.offerUiId);
  }, [values.offerUiId]);

  const formikProps = {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    errors,
    touched,
    setFieldValue,
    setTouched,
    validateField,
    validateForm
  };

  const rollingOfferTabs = useMemo(
    () => [
      { value: ERollingOfferFormState.GENERAL, label: 'Settings' },
      { value: ERollingOfferFormState.CONFIGURATION, label: 'Configuration' },
      { value: ERollingOfferFormState.SCHEDULE, label: 'Schedule' }
    ],
    []
  );

  const { handleTabChange } = useTabTracking({
    sidebarTab: Rolling.title,
    tabs: rollingOfferTabs,
    customNavigate: (value) => setTab(value as ERollingOfferFormState)
  });

  const updateContent = async (): Promise<void> => {
    if (!hasMissingProductDetails && submitForm) {
      await submitForm();
    } else {
      if (edit && values.active) {
        setIsUpdateDialogOpen(true);
      } else {
        setIsSaveDialogOpen(true);
      }
    }
  };

  const formTitle = useMemo(
    () =>
      `${edit ? 'Edit' : 'Create'} Rolling Offer${edit && getOffer.data?.result?.name ? ` (${getOffer.data.result.name})` : ''}`,
    [edit, getOffer.data?.result?.name]
  );

  const areAllBarPointsValid = useMemo(() => {
    return productsSequence.every(
      (_, index) => barPointsValidity[index] !== false
    );
  }, [productsSequence, barPointsValidity]);

  const isFormValid = useMemo(
    () =>
      !isLoading &&
      isValid &&
      canUserEdit &&
      areAllBarPointsValid &&
      (values.schedule?.permanent || (values.schedule?.timeFrames?.length ?? 0) > 0) &&
      productsSequence.length >= MIN_ROLLING_SUB_OFFERS &&
      (!edit || dirty),
    [
      isLoading,
      isValid,
      canUserEdit,
      areAllBarPointsValid,
      values.schedule?.permanent,
      values.schedule?.timeFrames?.length,
      productsSequence.length,
      edit,
      dirty
    ]
  );

  return (
    <OfferFormLayout
      title={formTitle}
      isFormValid={isFormValid}
      onSave={() => updateContent()}
      onClose={() => navigate('../')}
      previewContent={
        <div className="w-1/2 bg-base-muted flex items-center justify-center">
          <RollingOfferMock
            currentOfferUI={currentOfferUI}
            productsSequence={productsSequence}
          />
        </div>
      }
      extraContent={
        <RollingOfferModals
          isSaveDialogOpen={isSaveDialogOpen}
          isUpdateDialogOpen={isUpdateDialogOpen}
          setIsSaveDialogOpen={setIsSaveDialogOpen}
          setIsUpdateDialogOpen={setIsUpdateDialogOpen}
          submitForm={submitForm}
        />
      }
    >
      {!getImages.isLoading && getImages.data && (
        <Tabs
                className="h-full pt-10 pb-10 w-[528px]"
                value={tab}
                onValueChange={handleTabChange}
              >
                <FormSectionsTabs
                  currentTab={tab}
                  tabs={[
                    {
                      value: ERollingOfferFormState.GENERAL,
                      label: 'Settings',
                      disabled: getOffer.isLoading && edit
                    },
                    {
                      value: ERollingOfferFormState.CONFIGURATION,
                      label: 'Configuration',
                      disabled: getOffer.isLoading && edit
                    },
                    {
                      value: ERollingOfferFormState.SCHEDULE,
                      label: 'Schedule',
                      disabled: getOffer.isLoading && edit
                    }
                  ]}
                />
                <div className="flex-1 flex justify-center">
                  <TabsContent
                    value={ERollingOfferFormState.GENERAL}
                    className="mt-9 w-full"
                  >
                    <SettingsTab
                      formikProps={formikProps}
                      rollingOfferId={rollingOfferId}
                      edit={edit}
                      offerDesignOptions={offerDesignOptions}
                      chosenSegment={chosenSegment}
                      setChosenSegment={setChosenSegment}
                      data={data}
                      productsSequence={productsSequence}
                      setProductsSequence={setProductsSequence}
                      dup={dup}
                    />
                  </TabsContent>
                  <TabsContent
                    value={ERollingOfferFormState.CONFIGURATION}
                    className="mt-9 w-full"
                  >
                    <ConfigurationTab
                      formikProps={formikProps}
                      rollingOfferId={rollingOfferId}
                      productsSequence={productsSequence}
                      setProductsSequence={setProductsSequence}
                      setBarPointsValidity={setBarPointsValidity}
                    />
                  </TabsContent>
                  <TabsContent
                    value={ERollingOfferFormState.SCHEDULE}
                    className="mt-9 w-full"
                  >
                    <ScheduleTab
                      formikProps={formikProps}
                      rollingOfferId={rollingOfferId}
                      edit={edit}
                    />
                  </TabsContent>
                </div>
              </Tabs>
      )}
    </OfferFormLayout>
  );
};

export default RollingOfferForm;
